import { Tooltip } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslations } from "adapters/i18n";
import { CustomTableColumn } from "features/shared/components";
import React, { useMemo } from "react";
import type {
  EnrichedAnimalCategory,
  Goal,
  LivestockInfoFromFarm,
} from "shared/types";
import type {
  LotsPermissions,
  LotsTableRowChange,
  LotsTableRowErrors,
} from "../lots.types";
import {
  getValueByColumn,
  LotListTableColumn,
  LotSanity,
} from "../utils/lots.utils";
import GoalColumnCell from "../views/lotsListTable/components/goalColumnCell";
import GoalWeightColumnCell from "../views/lotsListTable/components/goalWeightColumnCell";
import LotColumnCell from "../views/lotsListTable/components/lotColumnCell";
import MeasuredWeightColumnCell from "../views/lotsListTable/components/measuredWeightColumnCell";
import ProjectedWeightColumnCell from "../views/lotsListTable/components/projectedWeightColumnCell";
import SanityColumnCell from "../views/lotsListTable/components/sanityColumnCell";
import WeighingDateColumnCell from "../views/lotsListTable/components/weighingDateColumnCell";

type UseLotLisTableProps = {
  enrichedAnimalCategories: EnrichedAnimalCategory[];
  lotGoals: Goal[];
  lotsPermissions: LotsPermissions;
  onBlurRowField: <T extends keyof LotsTableRowErrors[number]>(
    lotId: number,
    field: T,
  ) => void;
  onOpenWeightProjectionDetails: (row: LivestockInfoFromFarm) => void;
  onRowFieldChange: <T extends keyof LotsTableRowChange[number]>(
    lotId: number,
    field: T,
    value: LotsTableRowChange[number][T],
  ) => void;
  onRowFieldError: <T extends keyof LotsTableRowErrors[number]>(
    lotId: number,
    field: T,
    error: string | null,
  ) => void;
  tableChangeErrors: LotsTableRowErrors;
  tableChanges: LotsTableRowChange;
};

export function useLotListTable({
  enrichedAnimalCategories,
  lotGoals,
  lotsPermissions,
  onBlurRowField,
  onOpenWeightProjectionDetails,
  onRowFieldChange,
  onRowFieldError,
  tableChangeErrors,
  tableChanges,
}: UseLotLisTableProps) {
  const { currentLanguage, t } = useTranslations();

  const goalOptions = useMemo(
    () => lotGoals.map((goal) => ({ label: goal.name, value: goal.id })),
    [lotGoals],
  );

  const handleSelectGoal = (
    goalId: number | null,
    row: LivestockInfoFromFarm,
  ) => {
    onRowFieldChange(row.id, "goalId", goalId);
  };

  const handleChangeGoalWeight = (
    weight: number | null,
    row: LivestockInfoFromFarm,
  ) => {
    onRowFieldChange(row.id, "goalWeight", weight);
  };

  const handleBlurGoalWeight = (row: LivestockInfoFromFarm) => {
    onBlurRowField(row.id, "goalWeight");
  };

  const handleChangeMeasuredWeight = (
    weight: number | null,
    row: LivestockInfoFromFarm,
  ) => {
    onRowFieldChange(row.id, "measuredWeight", weight);
  };

  const handleBlurMeasuredWeight = (row: LivestockInfoFromFarm) => {
    onBlurRowField(row.id, "measuredWeight");
  };

  const handleChangeWeighingDate = (
    date: string | null,
    row: LivestockInfoFromFarm,
  ) => {
    onRowFieldChange(row.id, "weighingDate", date);
  };

  const handleWeighingDateError = (
    error: string | null,
    row: LivestockInfoFromFarm,
  ) => {
    onRowFieldError(row.id, "weighingDate", error);
  };

  const getValueFromChange = <T,>(
    tableChangeValue: T | null | undefined,
    originalValue: T | null,
  ): T | null => {
    // Only display the original value if there is no change made therefore undefined
    if (tableChangeValue !== undefined) {
      return tableChangeValue;
    }

    return originalValue;
  };

  const columns: CustomTableColumn<
    LivestockInfoFromFarm,
    typeof LotListTableColumn
  >[] = [
    {
      id: LotListTableColumn.Lot,
      label: t("livestock:lots"),
      isSortable: true,
      type: "text",
      getRowValue: (row) => getValueByColumn(row, LotListTableColumn.Lot),
      renderCell: (value, row) => <LotColumnCell row={row} value={value} />,
    },
    {
      id: LotListTableColumn.Quantity,
      label: t("common:amount"),
      isSortable: true,
      type: "number",
      getRowValue: (row) => getValueByColumn(row, LotListTableColumn.Quantity),
    },
    {
      id: LotListTableColumn.Sanity,
      label: t("livestock:sanity"),
      isSortable: true,
      type: "text",
      getRowValue: (row) => getValueByColumn(row, LotListTableColumn.Sanity),
      renderCell: (value) => <SanityColumnCell value={value as LotSanity} />,
      hide: !lotsPermissions.showSanityColumn,
    },
    {
      id: LotListTableColumn.Goal,
      label: t("common:goal"),
      isSortable: false,
      type: "number",
      getRowValue: (row) =>
        getValueFromChange(
          tableChanges[row.id]?.goalId,
          getValueByColumn(row, LotListTableColumn.Goal),
        ),
      renderCell: (value, row) => (
        <GoalColumnCell
          onChange={handleSelectGoal}
          options={goalOptions}
          row={row}
          value={value}
        />
      ),
    },
    {
      id: LotListTableColumn.GoalWeight,
      label: t("livestock:goal-weight"),
      isSortable: true,
      type: "number",
      getRowValue: (row) =>
        getValueFromChange(
          tableChanges[row.id]?.goalWeight,
          getValueByColumn(row, LotListTableColumn.GoalWeight),
        ),
      renderCell: (value, row) => (
        <GoalWeightColumnCell
          enrichedAnimalCategories={enrichedAnimalCategories}
          error={tableChangeErrors[row.id]?.goalWeight}
          hasChangedField={tableChanges[row.id]?.goalWeight !== undefined}
          onBlur={handleBlurGoalWeight}
          onChange={handleChangeGoalWeight}
          row={row}
          value={value}
        />
      ),
    },
    {
      id: LotListTableColumn.MeasuredWeight,
      label: t("livestock:measured-weight"),
      isSortable: true,
      type: "number",
      getRowValue: (row) =>
        getValueFromChange(
          tableChanges[row.id]?.measuredWeight,
          getValueByColumn(row, LotListTableColumn.MeasuredWeight),
        ),
      renderCell: (value, row) => (
        <MeasuredWeightColumnCell
          error={tableChangeErrors[row.id]?.measuredWeight}
          onBlur={handleBlurMeasuredWeight}
          onChange={handleChangeMeasuredWeight}
          row={row}
          value={value}
        />
      ),
    },
    {
      id: LotListTableColumn.WeighingDate,
      label: t("animalCategory:weighing-date"),
      isSortable: true,
      type: "text",
      styles: { maxWidth: "240px" },
      getRowValue: (row) =>
        getValueFromChange(
          tableChanges[row.id]?.weighingDate,
          getValueByColumn(row, LotListTableColumn.WeighingDate),
        ),
      renderCell: (value, row) => (
        <WeighingDateColumnCell
          error={tableChangeErrors[row.id]?.weighingDate}
          onChange={handleChangeWeighingDate}
          onError={handleWeighingDateError}
          row={row}
          value={value}
        />
      ),
    },
    {
      id: LotListTableColumn.ProjectedWeight,
      label: t("livestock:projected-weight"),
      isSortable: true,
      hide: !lotsPermissions.showWeightProjectionColumn,
      align: "center",
      trailingHeaderElement: (
        <Tooltip
          data-testid="projected-weight-info-tooltip"
          title={t("livestock:projected-weight-values-disaclaimer")}
          style={{ maxWidth: 60, verticalAlign: "middle" }}
          placement="right"
        >
          <InfoIcon color="primary" fontSize="inherit" />
        </Tooltip>
      ),
      type: "text",
      getRowValue: (row) =>
        getValueByColumn(
          row,
          LotListTableColumn.ProjectedWeight,
          currentLanguage.locale,
          tableChanges[row.id]?.measuredWeight,
        ),
      renderCell: (value, row) => (
        <ProjectedWeightColumnCell
          enrichedAnimalCategories={enrichedAnimalCategories}
          onOpenWeightProjectionDetails={onOpenWeightProjectionDetails}
          row={row}
          value={value}
        />
      ),
    },
  ];

  return { columns };
}
